import { useAuthStore } from '@/state/store/auth';
import { DOCX_MIME } from '../components/docx-generate/utils';

// ---------------------------------------------------------------------------
// DOCX → PDF conversion (custom Node backend proxy)
//
// The .docx is the only stored artifact for a fixed-DOCX contract; every PDF is
// derived on demand. The backend converts an uploaded .docx and streams the PDF
// bytes straight back, so nothing transient is written to storage.
//
// Errors still come back as the standard `{ success, statusCode, message, data }`
// envelope, so callers must branch on the response Content-Type rather than on
// `res.ok` alone — a proxy that answers a 502 with HTML must never be handed to
// a Blob and saved as a "PDF".
// ---------------------------------------------------------------------------

const BACKEND_BASE_URL =
  (import.meta.env.VITE_BACKEND_API_URL as string | undefined)?.replace(/\/$/, '') ?? '';

const CONVERT_BYTES_API = `${BACKEND_BASE_URL}/blocks/get-pdf-from-docx`;

/**
 * Auth headers for the backend proxy. NOTE: no `Content-Type` — the browser
 * must set the multipart boundary itself for a `FormData` body.
 */
function multipartAuthHeaders(): Record<string, string> {
  const { orgId, sigOrgId } = useAuthStore.getState();
  return {
    ...(orgId ? { 'x-blocks-org-id': orgId } : {}),
    ...(sigOrgId ? { 'x-sig-org-id': sigOrgId } : {}),
  };
}

const asBlob = (docx: Blob | Uint8Array): Blob =>
  docx instanceof Blob ? docx : new Blob([docx as BlobPart], { type: DOCX_MIME });

/**
 * Convert a .docx to PDF and return the raw PDF bytes.
 *
 * Callers consume bytes for every purpose — a Blob for download, base64 for the
 * signature rollout, pdf.js for anchor detection, an object URL for preview —
 * so no PDF is ever persisted.
 */
export async function convertDocxToPdfBytes(
  docx: Blob | Uint8Array,
  fileName: string
): Promise<Uint8Array> {
  const formData = new FormData();
  formData.append('file', asBlob(docx), fileName);

  const response = await fetch(CONVERT_BYTES_API, {
    method: 'POST',
    credentials: 'include',
    headers: multipartAuthHeaders(),
    body: formData,
  });

  // A JSON body means the error envelope, whatever the status code.
  const contentType = response.headers.get('content-type') ?? '';
  if (!response.ok || contentType.includes('application/json')) {
    const wrapped = await response.json().catch(() => null);
    throw new Error(
      wrapped?.message || `Failed to convert document to PDF (${response.status}).`
    );
  }

  return new Uint8Array(await response.arrayBuffer());
}

// ---------------------------------------------------------------------------
// Derived-PDF cache
//
// A locked contract's .docx never changes, so its PDF is deterministic. Actions
// that each need the PDF (download, print, send, AI analysis) would otherwise
// pay for a conversion apiece; cache the in-flight promise per stored .docx so
// repeated actions in one session convert once.
//
// Keyed by docxFileId, which changes only when new bytes are stored — so an
// edit naturally invalidates the entry rather than serving a stale PDF.
// ---------------------------------------------------------------------------

const derivedPdfCache = new Map<string, Promise<Uint8Array>>();

/**
 * Fetch a contract's stored .docx and convert it to PDF bytes.
 *
 * This is the single way to obtain a viewable/sendable PDF for a fixed-DOCX
 * contract outside the review page, replacing the old stored `Document.url`
 * (which went stale the moment the document was edited).
 */
export async function getContractPdfBytes({
  docxUrl,
  docxFileId,
  fileName,
}: {
  docxUrl: string;
  /** Cache key. Omit to force a fresh conversion. */
  docxFileId?: string;
  fileName: string;
}): Promise<Uint8Array> {
  const cached = docxFileId ? derivedPdfCache.get(docxFileId) : undefined;
  if (cached) return cached;

  const work = (async () => {
    const res = await fetch(docxUrl);
    if (!res.ok) {
      throw new Error(`Failed to load the document (${res.status}).`);
    }
    const docxBytes = new Uint8Array(await res.arrayBuffer());
    return convertDocxToPdfBytes(docxBytes, fileName);
  })();

  if (docxFileId) {
    derivedPdfCache.set(docxFileId, work);
    // Don't cache failures — the next attempt should retry.
    work.catch(() => derivedPdfCache.delete(docxFileId));
  }

  return work;
}

/** Build a short-lived object URL for derived PDF bytes. Caller revokes it. */
export const pdfObjectUrl = (bytes: Uint8Array): string =>
  URL.createObjectURL(new Blob([bytes as BlobPart], { type: 'application/pdf' }));
